import {
  Box,
  BoxProps,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  StackProps,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Assessment,
  AssessmentAverageResult,
  AssessmentAverageSubjectResult,
  AssessmentMethodScore,
  AssessmentMethodType,
  AssessmentsGrade,
  AssessmentWithEntries,
  isAssessmentMethodGrade,
  SubjectAverageScore,
} from '@schooly/api';
import { useGridApi } from '@schooly/components/data-grid';
import { useNotifications } from '@schooly/components/notifications';
import { AssessmentStatuses, DELAY_BEFORE_HIDE_ICON_DONE } from '@schooly/constants';
import {
  CellProps,
  DropdownCell,
  InformationIcon,
  NewTabIcon,
  PercentCell,
  ScoreCell,
  SimpleButton,
  TooltipMenu,
} from '@schooly/style';
import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { getScorePercentageValue } from '../../../../components/uikit-components/AssessmentEntryMethods/utils';
import {
  createGradeFinder,
  createPercentageColorFinder,
  getCellTextColor,
  mapColorIdxToColor,
} from './colouredCells';
import { MarkbookSubject } from './types';
import { findAverageResult, findAverageSubjectResult, getAverageResultsCellKey } from './utils';

export type AverageResultsAssessmentColumnData = {
  type: 'assessment';
  assessment: Assessment | AssessmentWithEntries;
  subject?: MarkbookSubject;
  methodId: string;
};

export type AverageResultsAverageColumnData = {
  type: 'average';
  subject: MarkbookSubject;
  average: number | null;
};

export type AverageResultsColumnData =
  | AverageResultsAssessmentColumnData
  | AverageResultsAverageColumnData;

type AverageResultsProps = {
  fixedColumn?: AverageResultsColumnData;
  columns: AverageResultsColumnData[];
  averageData: AssessmentAverageResult[];
  subjectAverageData: AssessmentAverageSubjectResult[];
  studentColumnWidth: number;
  methodsWidth?: [number, number];
  rowHeight?: number;
  isLoading: boolean;
  gradeFinders: Map<string, ReturnType<typeof createGradeFinder>>;
  setBenchmarkEnabled: (enabled: boolean) => void;
  setBenchmarkAverage: (average: AssessmentAverageResult) => void;
  setBenchmarkSubjectAverage: (average: AssessmentAverageSubjectResult) => void;
  benchmarkEnabled: boolean;
  gradeList: Record<string, AssessmentsGrade>;
  anchorOffset?: number;
  showPercentage?: boolean;
};

export const AverageResults: FC<AverageResultsProps> = ({
  fixedColumn,
  columns,
  averageData,
  subjectAverageData,
  isLoading,
  studentColumnWidth,
  methodsWidth = [224, 112],
  rowHeight = 60,
  gradeFinders,
  setBenchmarkEnabled,
  setBenchmarkAverage,
  setBenchmarkSubjectAverage,
  benchmarkEnabled,
  gradeList,
  anchorOffset = 16,
  showPercentage = false,
}) => {
  const { formatMessage } = useIntl();
  const apiRef = useGridApi();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (!apiRef) return;

    const averageScrollContainer = scrollContainerRef.current;
    if (!averageScrollContainer) return;

    const scrollPosition = apiRef.current.getScrollPosition?.();
    if (scrollPosition) {
      averageScrollContainer.style.transform = `translateX(-${scrollPosition.left}px)`;
    }
  }, [apiRef]);

  const compareAverage = useMemo(() => {
    if (!fixedColumn) return undefined;
    switch (fixedColumn.type) {
      case 'assessment': {
        return findAverageResult(averageData, fixedColumn);
      }
      case 'average': {
        return undefined;
      }
    }
  }, [averageData, fixedColumn]);

  const getBackgroundColor = useCallback(
    (average: AssessmentAverageResult) => {
      if (!fixedColumn) return undefined;

      let colorFinder: ReturnType<typeof createPercentageColorFinder> | undefined;

      switch (fixedColumn.type) {
        case 'assessment': {
          if (!compareAverage) break;
          colorFinder = createPercentageColorFinder(getPercentValueOfAverage(compareAverage));

          break;
        }
        case 'average': {
          if (fixedColumn.average === null) break;
          colorFinder = createPercentageColorFinder(fixedColumn.average);
          break;
        }
      }

      if (!colorFinder) return undefined;

      const colorIdx = colorFinder(getPercentValueOfAverage(average));
      return mapColorIdxToColor(colorIdx) ?? undefined;
    },
    [fixedColumn, compareAverage],
  );

  useEffect(() => {
    if (!apiRef?.current) return;

    const unsubscribe = apiRef.current.subscribeEvent('scrollPositionChange', handleScroll);
    return () => unsubscribe();
  }, [apiRef, handleScroll]);

  const renderColumn = (data: AverageResultsColumnData) => {
    let methodWidth = methodsWidth[1];

    let dimmed = false;

    const checkIfComparable = (data: AverageResultsAssessmentColumnData) => {
      if (!fixedColumn) return false;
      switch (fixedColumn.type) {
        case 'assessment': {
          return (
            compareAverage?.method.method_type ===
              data.assessment.methods.find((m) => m.id === data.methodId)?.method_type &&
            (fixedColumn.subject ? fixedColumn.subject.id === data.subject?.id : true)
          );
        }
        case 'average': {
          return fixedColumn?.type === 'average' && fixedColumn?.subject.id === data.subject?.id;
        }
      }
    };

    const renderColumnContent = () => {
      switch (data.type) {
        case 'assessment': {
          dimmed =
            fixedColumn?.type === 'assessment' &&
            fixedColumn?.assessment.id === data.assessment.id &&
            fixedColumn.subject?.id === data.subject?.id &&
            fixedColumn?.methodId === data.methodId;
          methodWidth = methodsWidth[data.assessment.methods.length > 1 ? 1 : 0];

          if (benchmarkEnabled)
            return (
              <AssessmentsBenchmarkRow
                averageData={averageData}
                data={data}
                gradeList={gradeList}
                gradeFinders={gradeFinders}
                onUpdateAverage={setBenchmarkAverage}
                methodWidth={methodWidth}
                anchorOffset={anchorOffset}
                showPercentage={showPercentage}
              />
            );

          const isComparable = checkIfComparable(data);

          return (
            <AssessmentsAverageRow
              data={data}
              getBackgroundColor={!dimmed && isComparable ? getBackgroundColor : undefined}
              isLoading={isLoading}
              averageData={averageData}
              gradeFinders={gradeFinders}
              showPercentage={showPercentage}
              dimmed={dimmed}
            />
          );
        }

        case 'average': {
          if (benchmarkEnabled)
            return (
              <AverageBenchmarkRow
                averageData={subjectAverageData}
                data={data}
                onUpdateAverage={setBenchmarkSubjectAverage}
              />
            );

          dimmed = fixedColumn?.type === 'average' && fixedColumn?.subject.id === data.subject.id;
          return (
            <MarkbookAverageCell
              isDimmed={dimmed}
              average={{ average_score: data.average }}
              subject={data.subject}
            />
          );
        }
      }
    };

    return (
      <Stack
        key={getAverageResultsCellKey(data)}
        sx={(theme) => ({
          minWidth: methodWidth,
          height: rowHeight,
          width: methodWidth,
          alignItems: 'center',
          justifyContent: 'center',
          borderRight: theme.mixins.borderValue(),
          backgroundColor: dimmed ? 'background.default' : undefined,
          padding: !benchmarkEnabled ? theme.spacing(0.5) : 0,
        })}
      >
        {renderColumnContent()}
      </Stack>
    );
  };
  const renderFixedColumn = () => {
    if (!fixedColumn) return null;

    const methodWidth = methodsWidth[1];

    const renderFixedColumnContent = () => {
      switch (fixedColumn.type) {
        case 'assessment':
          return (
            <AssessmentsAverageRow
              data={fixedColumn}
              isLoading={isLoading}
              averageData={averageData}
              gradeFinders={gradeFinders}
              showPercentage={showPercentage}
            />
          );
        case 'average':
          return (
            <MarkbookAverageCell
              average={{ average_score: fixedColumn.average }}
              subject={fixedColumn.subject}
            />
          );
      }
    };

    return (
      <Stack
        sx={(theme) => ({
          position: 'relative',
          minWidth: methodWidth,
          height: rowHeight,
          width: methodWidth,
          alignItems: 'center',
          justifyContent: 'center',
          borderRight: theme.mixins.borderValue(),
          borderLeft: theme.mixins.borderValue(),
          pointerEvents: 'none',
          borderColor: theme.palette.primary.main,
        })}
      >
        {renderFixedColumnContent()}
      </Stack>
    );
  };

  return (
    <Stack
      direction="row"
      sx={(theme) => ({
        height: rowHeight,
        backgroundColor: theme.palette.background.paper,
        alignItems: 'center',
      })}
    >
      <Stack
        sx={(theme) => ({
          flex: 1,
          minWidth: studentColumnWidth,
          borderRight: theme.mixins.borderValue(),
          backgroundColor: theme.palette.background.paper,
          justifyContent: 'center',
          height: '100%',
          pr: 1,
        })}
      >
        <TooltipMenu
          currentValue={
            benchmarkEnabled
              ? formatMessage({ id: 'assessments-markbook-benchmark' })
              : formatMessage({ id: 'assessments-markbook-averageResult' })
          }
          options={[
            {
              value: formatMessage({ id: 'assessments-markbook-averageResult' }),
              onClick: () => setBenchmarkEnabled(false),
            },
            {
              value: formatMessage({ id: 'assessments-markbook-benchmark' }),
              onClick: () => setBenchmarkEnabled(true),
            },
          ]}
        />
      </Stack>

      {renderFixedColumn()}

      <Box sx={{ overflow: 'hidden' }}>
        <Stack ref={scrollContainerRef} direction="row" flex={1} alignItems="center">
          {columns.map(renderColumn)}
        </Stack>
      </Box>
    </Stack>
  );
};

type ScoreOutOfProps = {
  scoreMethod: AssessmentMethodScore;
  editAssessmentPath: string;
  scoreOutOfColor?: string;
};

const ScoreOutOf = ({
  scoreMethod,
  editAssessmentPath,
  scoreOutOfColor = 'common.grey',
}: ScoreOutOfProps) => {
  const { formatMessage } = useIntl();
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      sx={{ color: 'common.grey' }}
    >
      {scoreMethod.score_out_of ? (
        <Stack justifyContent="flex-end" height={16}>
          <Typography color={scoreOutOfColor} variant="caption">
            {'/'}
            {scoreMethod.score_out_of}
          </Typography>
        </Stack>
      ) : (
        <>
          {'/ '}
          <Tooltip
            componentsProps={{
              tooltip: {
                sx: {
                  padding: 1.25,
                  width: 200,
                },
              },
            }}
            title={
              <Stack gap={0.5}>
                <Typography>
                  {formatMessage({ id: 'assessments-markbook-scoreOutOfNotSet' })}
                </Typography>
                <Link to={editAssessmentPath} target="_blank">
                  <SimpleButton
                    sx={(theme) => ({
                      marginTop: theme.spacing(1),
                      '& .MuiButton-startIcon .svg-icon': {
                        fontSize: theme.spacing(2),
                      },
                    })}
                    startIcon={<NewTabIcon />}
                  >
                    <Typography>{formatMessage({ id: 'assessments-editAssessment' })}</Typography>
                  </SimpleButton>
                </Link>
              </Stack>
            }
          >
            <IconButton inverse sx={{ ml: 0.25 }}>
              <InformationIcon />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Stack>
  );
};

type AverageResultsWrapperProps = { modalView?: boolean; opened?: boolean } & BoxProps &
  PropsWithChildren;

export const AverageResultsWrapper: FC<AverageResultsWrapperProps> = ({
  children,
  modalView,
  opened = true,
  sx,
  ...boxProps
}) => {
  const baseStyles = {
    position: 'relative',
    backgroundColor: 'background.paper',
    overflow: 'hidden',
    opacity: opened ? 1 : 0,
    boxShadow: opened ? '0px -10px 20px 0px #25275A0D' : 'none',
  };

  return (
    <Collapse in={opened} timeout={300}>
      <Box
        {...boxProps}
        sx={[
          (theme) => ({
            ...baseStyles,
            ...(modalView
              ? {
                  mx: -3,
                  px: 3,
                }
              : {
                  mx: theme.spacing(-3),
                  px: theme.spacing(3),
                  mb: theme.spacing(0),
                }),
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {children}
      </Box>
    </Collapse>
  );
};

type AssessmentsAverageRowProps = {
  data: AverageResultsAssessmentColumnData;
  getBackgroundColor?: (average: AssessmentAverageResult) => string | undefined;
  isLoading: boolean;
  averageData: AssessmentAverageResult[];
  gradeFinders: Map<string, ReturnType<typeof createGradeFinder>>;
  showPercentage?: boolean;
  dimmed?: boolean;
};
const AssessmentsAverageRow: FC<AssessmentsAverageRowProps> = ({
  data,
  getBackgroundColor,
  isLoading,
  averageData,
  gradeFinders,
  showPercentage,
  dimmed = false,
}) => {
  const average = findAverageResult(averageData, data);

  const renderMethodContent = (
    average: AssessmentAverageResult,
    assessment: Assessment | AssessmentWithEntries,
  ) => {
    if (!average.average_value)
      return (
        <Typography variant="h3" color="common.grey">
          -
        </Typography>
      );

    const selectListId = average.method.select_list_id;
    const findGrade = selectListId ? gradeFinders.get(selectListId) : undefined;

    const backgroundColor = getBackgroundColor?.(average);
    const [textColor, scoreOutOfColor] = (backgroundColor && getCellTextColor(backgroundColor)) ?? [
      'primary.main',
      undefined,
    ];
    const showScorePercentage = showPercentage && average.method.score_out_of;

    return (
      <Stack
        direction="row"
        alignItems="center"
        sx={{ backgroundColor, width: '100%', height: '100%', borderRadius: '4px' }}
        justifyContent="center"
        gap={0.5}
      >
        <Typography variant="h3" color={dimmed ? 'common.grey6' : textColor}>
          {isAssessmentMethodGrade(average.method)
            ? findGrade?.(average.average_value)?.name
            : null}
        </Typography>
        {average.method.method_type === AssessmentMethodType.Score ? (
          <>
            <Typography variant="h3" color={dimmed ? 'common.grey6' : textColor}>
              {showScorePercentage
                ? `${getScorePercentageValue(
                    average.average_value,
                    average.method.score_out_of ?? 0,
                  )}%`
                : average.average_value}
            </Typography>

            {!showScorePercentage && (
              <ScoreOutOf
                scoreMethod={average.method}
                scoreOutOfColor={scoreOutOfColor}
                editAssessmentPath={`/assessments/${assessment.id}/${
                  assessment.status !== AssessmentStatuses.Published ? 'edit' : ''
                }`}
              />
            )}
          </>
        ) : null}
      </Stack>
    );
  };

  if (isLoading) {
    return (
      <Skeleton
        variant="rectangular"
        width="60px"
        height={16}
        sx={{ '&&': { borderRadius: '4px' } }}
      />
    );
  }
  return average ? renderMethodContent(average, data.assessment) : '-';
};

type AssessmentsBenchmarkRowProps = {
  averageData: AssessmentAverageResult[];
  data: AverageResultsAssessmentColumnData;
  onUpdateAverage: (average: AssessmentAverageResult) => void;
  gradeList: Record<string, AssessmentsGrade>;
  gradeFinders: Map<string, ReturnType<typeof createGradeFinder>>;
  methodWidth: number;
  anchorOffset: number;
  showPercentage?: boolean;
};

const AssessmentsBenchmarkRow: FC<AssessmentsBenchmarkRowProps> = ({
  averageData,
  data,
  onUpdateAverage,
  gradeList,
  gradeFinders,
  methodWidth,
  anchorOffset,
  showPercentage,
}) => {
  const average = useMemo(() => findAverageResult(averageData, data), [averageData, data]);
  const [averageStatus, setAverageStatus] = useState<CellProps<number>['status']>(null);
  const { formatMessage } = useIntl();
  const { showError } = useNotifications();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
    }
    if (event.key === 'Enter') {
      (event.target as HTMLElement).blur();
    }
  };

  const handleScoreUpdate = async (score: number | null, average: AssessmentAverageResult) => {
    return new Promise<void>((resolve, reject) => {
      if (score && average.method.score_out_of && score > average.method.score_out_of) {
        setAverageStatus('error');
        showError({
          message: formatMessage({ id: 'assessments-markbook-benchmarkScoreError' }),
        });
        setTimeout(() => {
          setAverageStatus(null);
        }, DELAY_BEFORE_HIDE_ICON_DONE);
        return reject();
      }

      onUpdateAverage({
        ...average,
        average_value: score,
      });
      return resolve();
    });
  };

  const renderMethodContent = (average: AssessmentAverageResult) => {
    if (average.method.method_type === AssessmentMethodType.Score) {
      return (
        <ScoreCell
          key={`${getAverageResultsCellKey(data)}-${average.average_value}`}
          value={average.average_value ?? null}
          scoreOutOf={average.method.score_out_of}
          status={averageStatus}
          onUpdate={(score) => handleScoreUpdate(score, average)}
          getDisplayValue={
            showPercentage && !averageStatus
              ? (value, scoreOutOf) => `${getScorePercentageValue(value, scoreOutOf)}%`
              : undefined
          }
        />
      );
    }

    if (average.method.method_type === AssessmentMethodType.Grade) {
      const selectListId = average.method.select_list_id;
      const grade = selectListId ? gradeList[selectListId] : null;

      if (!grade) return null;

      const findGrade = selectListId ? gradeFinders.get(selectListId) : undefined;
      const gradeOption = findGrade?.(average.average_value ?? 0) ?? null;
      return (
        <DropdownCell
          value={average.average_value ? gradeOption : null}
          options={grade.options}
          status={null}
          width={methodWidth}
          textColor={[average.average_value === null ? 'common.grey2' : 'primary.main', undefined]}
          canReset={average.average_value !== null}
          height={60}
          onUpdate={async (option) => {
            onUpdateAverage({
              ...average,
              average_value: option?.value ?? null,
            });
          }}
          anchorOffset={anchorOffset}
        />
      );
    }

    return (
      <Typography variant="h3" color="common.grey">
        -
      </Typography>
    );
  };
  return (
    <Stack
      onKeyDown={handleKeyDown}
      alignItems="center"
      justifyContent="center"
      sx={{ width: '100%', height: '100%' }}
    >
      {average ? renderMethodContent(average) : '-'}
    </Stack>
  );
};

type AverageBenchmarkRowProps = {
  averageData: AssessmentAverageSubjectResult[];
  data: AverageResultsAverageColumnData;
  onUpdateAverage: (average: AssessmentAverageSubjectResult) => void;
};

const AverageBenchmarkRow: FC<AverageBenchmarkRowProps> = ({
  averageData,
  data,
  onUpdateAverage,
}) => {
  const average = useMemo(() => findAverageSubjectResult(averageData, data), [averageData, data]);
  const [averageStatus, setAverageStatus] = useState<CellProps<number>['status']>(null);
  const { showError } = useNotifications();
  const { formatMessage } = useIntl();
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
    }
    if (event.key === 'Enter') {
      (event.target as HTMLElement).blur();
    }
  };

  const handleAverageUpdate = async (
    score: number | null,
    average: AssessmentAverageSubjectResult,
  ) => {
    return new Promise<void>((resolve, reject) => {
      if (score !== null && score > 100) {
        setAverageStatus('error');
        showError({
          message: formatMessage({ id: 'assessments-markbook-benchmarkPercentageError' }),
        });
        setTimeout(() => {
          setAverageStatus(null);
        }, DELAY_BEFORE_HIDE_ICON_DONE);
        return reject();
      }

      onUpdateAverage({
        ...average,
        average_score: score,
      });
      return resolve();
    });
  };

  return (
    <Stack
      onKeyDown={handleKeyDown}
      alignItems="center"
      justifyContent="center"
      sx={{ width: '100%', height: '100%' }}
    >
      {average ? (
        <PercentCell
          canEdit={true}
          key={`${getAverageResultsCellKey(data)}-${average.average_score}`}
          value={average.average_score}
          status={averageStatus}
          onUpdate={(score) => handleAverageUpdate(score, average)}
        />
      ) : (
        '—'
      )}
    </Stack>
  );
};

type MarkbookAverageCellProps = {
  subject: MarkbookSubject;
  average?: Pick<SubjectAverageScore, 'average_score'>;
  isDimmed?: boolean;
} & StackProps;

const MarkbookAverageCell: FC<MarkbookAverageCellProps> = ({
  average,
  subject,
  isDimmed,
  ...props
}) => {
  const score = average?.average_score;

  return (
    <Stack justifyContent="center" alignItems="center" {...props}>
      <Typography variant="h3" color={isDimmed || !score ? 'common.grey6' : 'primary.main'}>
        {score ? `${score}%` : '—'}
      </Typography>
    </Stack>
  );
};

const getPercentValueOfAverage = (average: AssessmentAverageResult) => {
  switch (average.method.method_type) {
    case AssessmentMethodType.Score:
      const {
        average_value,
        method: { score_out_of },
      } = average;

      if (average_value === null || score_out_of === null || score_out_of === 0) return 0;
      return (average_value / score_out_of) * 100;
    case AssessmentMethodType.Grade:
      return average.average_value ?? 0;
    default:
      return 0;
  }
};
